import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Trash2, Star, CheckCircle } from 'lucide-react';
import ConfirmationModal from './ConfirmationModal';
import api from '../services/api';

const AdminReviews = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });

    useEffect(() => {
        fetchReviews();
    }, []);

    const fetchReviews = async () => {
        try {
            const response = await api.get('/admin/reviews');
            setReviews(response.data);
            setLoading(false);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            setLoading(false);
        }
    };

    const handleDeleteClick = (id) => {
        setDeleteModal({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        const id = deleteModal.id;
        try {
            await api.delete(`/reviews/${id}`);
            
            setReviews(reviews.filter(review => review.id !== id));
            setDeleteModal({ isOpen: false, id: null });
            toast.success("Review deleted");
        } catch (err) {
            toast.error('Error deleting review: ' + (err.response?.data?.message || err.message));
        }
    };

    if (loading) return <div className="p-8 text-center text-raya-text-tertiary">Loading reviews...</div>;
    if (error) return <div className="p-8 text-center text-raya-text-tertiary">Error: {error}</div>;

    return (
        <div className="bg-raya-card rounded-lg shadow-sm border border-raya-border overflow-hidden">
            <div className="px-6 py-4 border-b border-raya-border flex justify-between items-center bg-raya-bg-off">
                <h2 className="text-xl font-bold text-raya-text-primary">Product Reviews</h2>
                <span className="text-sm text-raya-text-tertiary">Total: {reviews.length}</span>
            </div>
            
            {reviews.length === 0 ? (
                <div className="p-8 text-center text-raya-text-tertiary">
                    No reviews found.
                </div>
            ) : (
                <div className="divide-y divide-raya-border">
                    {reviews.map((review) => (
                        <div key={review.id} className="p-6 transition hover:bg-raya-bg-off">
                            <div className="flex justify-between items-start gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="flex text-raya-accent">
                                            {[...Array(5)].map((_, i) => (
                                                <Star key={i} size={14} fill={i < review.rating ? "currentColor" : "none"} className={i < review.rating ? "" : "text-raya-text-tertiary"} />
                                            ))}
                                        </div>
                                        <span className="text-sm font-medium text-raya-text-primary">
                                            {review.review_title}
                                        </span>
                                        {review.verified_purchase && (
                                            <span className="flex items-center text-xs text-raya-accent bg-raya-accent/10 px-1.5 py-0.5 rounded border border-raya-accent/20">
                                                <CheckCircle size={10} className="mr-1" /> Verified
                                            </span>
                                        )}
                                    </div>
                                    
                                    <p className="text-raya-text-secondary text-sm mb-2">
                                        "{review.review_text}"
                                    </p>
                                    
                                    <div className="flex items-center gap-4 text-xs text-raya-text-tertiary">
                                        <span>By: {review.customer_name} ({review.customer_email})</span>
                                        <span>•</span>
                                        <span>Product: {review.product_name}</span>
                                        <span>•</span>
                                        <span>{new Date(review.created_at).toLocaleDateString()}</span>
                                    </div>
                                </div>
                                
                                <button 
                                    onClick={() => handleDeleteClick(review.id)}
                                    className="p-2 text-raya-text-tertiary hover:text-raya-accent transition"
                                    title="Delete Review"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmationModal 
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Delete Review"
                message="Are you sure you want to delete this review? This action cannot be undone."
            />
        </div>
    );
};

export default AdminReviews;
